import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import axios from 'axios';
import { 
  Sparkles, 
  Brain, 
  MessageSquare, 
  Shield, 
  Clock, 
  AlertTriangle,
  Settings,
  Copy,
  Send,
  RefreshCw,
  BarChart3,
  Lightbulb,
  MapPin,
  Share2,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
  Wand2,
  Coins
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AIAssistant() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState('content');
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [credits, setCredits] = useState(null);
  
  // Content Studio State
  const [contentType, setContentType] = useState('post');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [platform, setPlatform] = useState('socyy');
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeEmoji, setIncludeEmoji] = useState(true);
  const [includeCta, setIncludeCta] = useState(false);
  const [context, setContext] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  // Engagement Copilot State
  const [originalMessage, setOriginalMessage] = useState('');
  const [messageType, setMessageType] = useState('comment');
  const [replySuggestions, setReplySuggestions] = useState([]);
  const [generatingReply, setGeneratingReply] = useState(false);
  
  // Analytics State
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Publishing State
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishTo, setPublishTo] = useState(['socyy']);
  const [localeAddress, setLocaleAddress] = useState('');
  const [localeType, setLocaleType] = useState('deal');

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      const data = res.data;
      setUser(data);
      
      // Check if user has access
      const allowedTiers = ['enterprise', 'supremium', 'enterprise_supremium', 'business', 'nonprofit_enterprise', 'nonprofit_supremium'];
      if (!allowedTiers.includes(data.subscription_tier) && !data.is_admin) {
        toast.error('AI Assistant requires Business or Enterprise subscription');
        navigate('/pricing');
        return;
      }
      
      // Fetch dashboard and settings
      await Promise.all([
        fetchDashboard(),
        fetchSettings(),
        fetchConsentStatus(),
        fetchCredits()
      ]);
      
    } catch (err) {
      console.error(err);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai-assistant/dashboard`, { withCredentials: true });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai-assistant/settings`, { withCredentials: true });
      setSettings(res.data.settings);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchConsentStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/ai-assistant/consent`, { withCredentials: true });
      setConsentStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch consent:', err);
    }
  };

  const fetchCredits = async () => {
    try {
      const res = await axios.get(`${API_URL}/credits/balance`, { withCredentials: true });
      setCredits(res.data);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    }
  };

  const giveConsent = async () => {
    try {
      const res = await axios.post(`${API_URL}/ai-assistant/consent?data_usage_allowed=true`, {}, { withCredentials: true });
      toast.success('AI consent recorded successfully');
      await fetchConsentStatus();
    } catch (err) {
      toast.error('Failed to record consent');
    }
  };

  const generateContent = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    
    // Check credits first
    if (credits && credits.balance < 1) {
      toast.error('Insufficient credits. Please purchase more credits.');
      navigate('/credits');
      return;
    }
    
    setGenerating(true);
    setSuggestions([]);
    
    try {
      const res = await axios.post(`${API_URL}/ai-assistant/content/generate`, {
        content_type: contentType,
        topic,
        tone,
        length,
        include_hashtags: includeHashtags,
        include_emoji: includeEmoji,
        include_cta: includeCta,
        target_platform: platform,
        context: context || null
      }, { withCredentials: true });
      
      setSuggestions(res.data.suggestions || []);
      
      // Update credits balance from response
      if (res.data.credits) {
        setCredits(prev => prev ? { ...prev, balance: res.data.credits.balance_remaining } : null);
        toast.success(`Generated ${res.data.suggestions?.length || 0} suggestions (${res.data.credits.used} credits used)`);
      } else {
        toast.success(`Generated ${res.data.suggestions?.length || 0} suggestions`);
      }
    } catch (err) {
      // Handle insufficient credits error
      if (err.response?.status === 402) {
        toast.error('Insufficient credits. Redirecting to purchase...');
        navigate('/credits');
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const generateReply = async () => {
    if (!originalMessage.trim()) {
      toast.error('Please enter the message to reply to');
      return;
    }
    
    // Check credits first
    if (credits && credits.balance < 1) {
      toast.error('Insufficient credits. Please purchase more credits.');
      navigate('/credits');
      return;
    }
    
    setGeneratingReply(true);
    setReplySuggestions([]);
    
    try {
      const res = await axios.post(`${API_URL}/ai-assistant/engagement/suggest-reply`, {
        original_message: originalMessage,
        message_type: messageType,
        urgency: 'normal'
      }, { withCredentials: true });
      
      setReplySuggestions(res.data.suggestions || []);
      toast.success('Generated reply suggestions');
      
      // Refresh credits after usage
      fetchCredits();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Insufficient credits. Redirecting to purchase...');
        navigate('/credits');
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to generate replies');
    } finally {
      setGeneratingReply(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API_URL}/ai-assistant/analytics/overview?days=30`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const publishContent = async (suggestion) => {
    if (publishTo.includes('locale') && !localeAddress.trim()) {
      toast.error('Please enter an address for Locale');
      return;
    }
    
    setPublishing(true);
    
    try {
      const res = await axios.post(`${API_URL}/ai-assistant/publish`, {
        content: suggestion.content,
        suggestion_id: suggestion.id,
        publish_to: publishTo,
        locale_address: localeAddress || null,
        locale_type: localeType,
        hashtags: suggestion.hashtags || [],
        hubpost_platforms: []
      }, { withCredentials: true });
      
      toast.success(res.data.message || 'Content published successfully');
      setSelectedSuggestion(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  // Consent Required Screen
  if (consentStatus && !consentStatus.has_consented) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-8 h-8 text-violet-400" />
              </div>
              <CardTitle className="text-2xl text-white">AI Data Consent Required</CardTitle>
              <CardDescription className="text-slate-400">
                Before using the AI Assistant, we need your consent for data processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300 space-y-3">
                <p className="font-medium text-white">This AI assistant operates under strict legal and ethical guidelines:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>FTC Compliance:</strong> AI-generated content is clearly labeled</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>GDPR/CCPA:</strong> Your data is protected and you control its use</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Transparency:</strong> You always know when AI is involved</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Human Oversight:</strong> Final decisions are always yours</span>
                  </li>
                </ul>
              </div>
              
              <Button 
                onClick={giveConsent} 
                className="w-full bg-violet-600 hover:bg-violet-700"
                data-testid="consent-button"
              >
                I Agree - Enable AI Assistant
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                You can withdraw consent at any time in Settings
              </p>
            </CardContent>
          </Card>
        </div>
        <FloatingDock />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pb-24">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">AI Assistant</h1>
                <p className="text-xs text-slate-400">Powered by GPT-5.2</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Credits Display */}
              <button 
                onClick={() => navigate('/credits')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
                data-testid="credits-display"
              >
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300 font-semibold">{credits?.balance || 0}</span>
                <span className="text-amber-400/70 text-xs">credits</span>
              </button>
              <Badge variant="outline" className="border-violet-500/50 text-violet-400">
                {user?.subscription_tier || 'Business'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Low Credits Warning */}
        {credits && credits.balance < 5 && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 text-sm">
                Low credits! You have {credits.balance} credits remaining.
              </span>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/credits')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Buy Credits
            </Button>
          </div>
        )}

        {/* Module Navigation */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
          {[
            { id: 'content', icon: Sparkles, label: 'Content Studio', color: 'violet' },
            { id: 'analytics', icon: BarChart3, label: 'Analytics', color: 'blue' },
            { id: 'engagement', icon: MessageSquare, label: 'Engagement', color: 'green' },
            { id: 'moderation', icon: Shield, label: 'Brand Guard', color: 'amber' },
            { id: 'scheduler', icon: Clock, label: 'Scheduler', color: 'cyan' },
            { id: 'crisis', icon: AlertTriangle, label: 'Crisis', color: 'red' }
          ].map((module) => (
            <button
              key={module.id}
              onClick={() => {
                setActiveModule(module.id);
                if (module.id === 'analytics' && !analytics) {
                  fetchAnalytics();
                }
              }}
              data-testid={`module-${module.id}`}
              className={`p-3 rounded-xl transition-all ${
                activeModule === module.id
                  ? `bg-${module.color}-500/20 border border-${module.color}-500/50`
                  : 'bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800'
              }`}
            >
              <module.icon className={`w-5 h-5 mx-auto mb-1 ${
                activeModule === module.id ? `text-${module.color}-400` : 'text-slate-400'
              }`} />
              <span className={`text-xs block ${
                activeModule === module.id ? 'text-white' : 'text-slate-400'
              }`}>
                {module.label}
              </span>
            </button>
          ))}
        </div>

        {/* Content Studio Module */}
        {activeModule === 'content' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Generator Panel */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-violet-400" />
                  Content Generator
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Generate AI-powered content for any platform
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Content Type</Label>
                    <Select value={contentType} onValueChange={setContentType}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="post">Social Post</SelectItem>
                        <SelectItem value="caption">Caption</SelectItem>
                        <SelectItem value="hashtags">Hashtags</SelectItem>
                        <SelectItem value="reply">Reply</SelectItem>
                        <SelectItem value="bio">Bio</SelectItem>
                        <SelectItem value="campaign">Campaign</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Platform</Label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="socyy">Socyy</SelectItem>
                        <SelectItem value="twitter">Twitter/X</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Topic / Subject *</Label>
                  <Input
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., Product launch announcement, Holiday sale..."
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="topic-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Additional Context (optional)</Label>
                  <Textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Any additional details to help generate better content..."
                    className="bg-slate-800 border-slate-700 text-white mt-1 min-h-[80px]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Tone</Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="witty">Witty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Length</Label>
                    <Select value={length} onValueChange={setLength}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="short">Short</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="long">Long</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={includeHashtags} 
                      onCheckedChange={setIncludeHashtags}
                      id="hashtags"
                    />
                    <Label htmlFor="hashtags" className="text-slate-300 text-sm">Hashtags</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={includeEmoji} 
                      onCheckedChange={setIncludeEmoji}
                      id="emoji"
                    />
                    <Label htmlFor="emoji" className="text-slate-300 text-sm">Emojis</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={includeCta} 
                      onCheckedChange={setIncludeCta}
                      id="cta"
                    />
                    <Label htmlFor="cta" className="text-slate-300 text-sm">Call-to-Action</Label>
                  </div>
                </div>
                
                <Button 
                  onClick={generateContent}
                  disabled={generating || !topic.trim()}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  data-testid="generate-button"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Content
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Suggestions Panel */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-400" />
                  AI Suggestions
                </CardTitle>
                <CardDescription className="text-slate-400">
                  {suggestions.length > 0 
                    ? `${suggestions.length} suggestions generated` 
                    : 'Your AI-generated content will appear here'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {suggestions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Enter a topic and click Generate to create content</p>
                  </div>
                ) : (
                  suggestions.map((suggestion, idx) => (
                    <div 
                      key={suggestion.id || idx}
                      className="bg-slate-800/50 rounded-lg p-4 space-y-3"
                      data-testid={`suggestion-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Badge variant="outline" className="border-violet-500/50 text-violet-400 text-xs">
                          AI Generated
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {Math.round((suggestion.ai_confidence || 0.85) * 100)}% confidence
                        </span>
                      </div>
                      
                      <p className="text-white text-sm whitespace-pre-wrap">
                        {suggestion.content}
                      </p>
                      
                      {suggestion.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {suggestion.hashtags.map((tag, i) => (
                            <span key={i} className="text-xs text-violet-400">{tag}</span>
                          ))}
                        </div>
                      )}
                      
                      {suggestion.estimated_engagement && (
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span>Engagement: {suggestion.estimated_engagement}</span>
                          {suggestion.best_time_to_post && (
                            <span>Best time: {suggestion.best_time_to_post}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2 pt-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(suggestion.content + (suggestion.hashtags?.length ? '\n\n' + suggestion.hashtags.join(' ') : ''))}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => setSelectedSuggestion(suggestion)}
                          className="bg-violet-600 hover:bg-violet-700"
                          data-testid={`publish-btn-${idx}`}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Publish
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Module */}
        {activeModule === 'analytics' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Analytics Brain</h2>
              <Button 
                onClick={fetchAnalytics}
                variant="outline"
                disabled={loadingAnalytics}
                className="border-slate-700 text-slate-300"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingAnalytics ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {analytics ? (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Performance Metrics</CardTitle>
                    <CardDescription className="text-slate-400">
                      Last {analytics.period_days} days
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { label: 'Total Posts', value: analytics.metrics.total_posts },
                        { label: 'Total Likes', value: analytics.metrics.total_likes },
                        { label: 'Comments', value: analytics.metrics.total_comments },
                        { label: 'Reposts', value: analytics.metrics.total_reposts },
                        { label: 'Avg Engagement', value: analytics.metrics.avg_engagement_per_post?.toFixed(1) },
                        { label: 'Followers', value: analytics.metrics.followers }
                      ].map((stat, i) => (
                        <div key={i} className="bg-slate-800/50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{stat.value}</p>
                          <p className="text-xs text-slate-400">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Brain className="w-5 h-5 text-blue-400" />
                      AI Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analytics.ai_insights?.map((insight, i) => (
                      <div 
                        key={i}
                        className={`p-3 rounded-lg border ${
                          insight.priority === 'high' 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : insight.type === 'positive'
                            ? 'bg-green-500/10 border-green-500/30'
                            : 'bg-blue-500/10 border-blue-500/30'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge 
                            variant="outline" 
                            className={
                              insight.priority === 'high' 
                                ? 'border-red-500/50 text-red-400' 
                                : insight.type === 'positive'
                                ? 'border-green-500/50 text-green-400'
                                : 'border-blue-500/50 text-blue-400'
                            }
                          >
                            {insight.priority}
                          </Badge>
                          <span className="text-sm font-medium text-white">{insight.title}</span>
                        </div>
                        <p className="text-xs text-slate-400 mb-2">{insight.insight}</p>
                        <p className="text-xs text-slate-300 flex items-center gap-1">
                          <ChevronRight className="w-3 h-3" />
                          {insight.action}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="py-12 text-center">
                  {loadingAnalytics ? (
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-400" />
                  ) : (
                    <>
                      <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                      <p className="text-slate-400">Click Refresh to load analytics</p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Engagement Copilot Module */}
        {activeModule === 'engagement' && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  Engagement Copilot
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Generate smart replies for comments and messages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300 text-sm">Message Type</Label>
                  <Select value={messageType} onValueChange={setMessageType}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="comment">Comment</SelectItem>
                      <SelectItem value="dm">Direct Message</SelectItem>
                      <SelectItem value="mention">Mention</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Original Message *</Label>
                  <Textarea
                    value={originalMessage}
                    onChange={(e) => setOriginalMessage(e.target.value)}
                    placeholder="Paste the message you want to reply to..."
                    className="bg-slate-800 border-slate-700 text-white mt-1 min-h-[120px]"
                    data-testid="original-message-input"
                  />
                </div>
                
                <Button 
                  onClick={generateReply}
                  disabled={generatingReply || !originalMessage.trim()}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="generate-reply-button"
                >
                  {generatingReply ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Suggest Replies
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white">Reply Suggestions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {replySuggestions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Enter a message and generate reply suggestions</p>
                  </div>
                ) : (
                  replySuggestions.map((reply, idx) => (
                    <div 
                      key={reply.id || idx}
                      className="bg-slate-800/50 rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="border-green-500/50 text-green-400">
                          {reply.tone}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {Math.round((reply.ai_confidence || 0.85) * 100)}% match
                        </span>
                      </div>
                      <p className="text-white text-sm">{reply.content}</p>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(reply.content)}
                        className="border-slate-700 text-slate-300"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Other modules - simplified views */}
        {activeModule === 'moderation' && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-400" />
                Brand Guardian
              </CardTitle>
              <CardDescription className="text-slate-400">
                AI-powered content moderation and brand safety checks
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center py-8">
              <Shield className="w-16 h-16 mx-auto mb-4 text-amber-400/50" />
              <p className="text-slate-400">Content moderation is automatically applied to all AI-generated content.</p>
              <p className="text-slate-500 text-sm mt-2">All content is checked for brand safety, legal compliance, and sentiment.</p>
            </CardContent>
          </Card>
        )}

        {activeModule === 'scheduler' && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Smart Scheduler
              </CardTitle>
              <CardDescription className="text-slate-400">
                AI-optimized posting times for maximum engagement
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center py-8">
              <Clock className="w-16 h-16 mx-auto mb-4 text-cyan-400/50" />
              <p className="text-slate-400">Generate content in Content Studio, then use Publish to schedule.</p>
              <p className="text-slate-500 text-sm mt-2">AI will suggest optimal posting times based on your audience.</p>
            </CardContent>
          </Card>
        )}

        {activeModule === 'crisis' && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Crisis Monitor
              </CardTitle>
              <CardDescription className="text-slate-400">
                Real-time reputation monitoring and crisis response
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center py-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
                <span className="text-lg text-white">All Clear</span>
              </div>
              <p className="text-slate-400">No negative sentiment spikes detected.</p>
              <p className="text-slate-500 text-sm mt-2">Crisis monitoring is active and will alert you if issues arise.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Publish Modal */}
      {selectedSuggestion && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-slate-900 border-slate-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-violet-400" />
                Publish Content
              </CardTitle>
              <CardDescription className="text-slate-400">
                Choose where to publish this AI-generated content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm text-white whitespace-pre-wrap">{selectedSuggestion.content}</p>
                {selectedSuggestion.hashtags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedSuggestion.hashtags.map((tag, i) => (
                      <span key={i} className="text-xs text-violet-400">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Publish To</Label>
                <div className="space-y-2">
                  {[
                    { id: 'socyy', label: 'Socyy Feed', icon: Share2 },
                    { id: 'locale', label: 'Locale (Events & Deals)', icon: MapPin }
                  ].map((opt) => (
                    <div 
                      key={opt.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        publishTo.includes(opt.id)
                          ? 'bg-violet-500/20 border border-violet-500/50'
                          : 'bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800'
                      }`}
                      onClick={() => {
                        if (publishTo.includes(opt.id)) {
                          setPublishTo(publishTo.filter(p => p !== opt.id));
                        } else {
                          setPublishTo([...publishTo, opt.id]);
                        }
                      }}
                    >
                      <opt.icon className={`w-5 h-5 ${
                        publishTo.includes(opt.id) ? 'text-violet-400' : 'text-slate-400'
                      }`} />
                      <span className={publishTo.includes(opt.id) ? 'text-white' : 'text-slate-300'}>
                        {opt.label}
                      </span>
                      {publishTo.includes(opt.id) && (
                        <CheckCircle2 className="w-4 h-4 text-violet-400 ml-auto" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {publishTo.includes('locale') && (
                <div className="space-y-3 p-3 bg-slate-800/50 rounded-lg">
                  <div>
                    <Label className="text-slate-300 text-sm">Locale Type</Label>
                    <Select value={localeType} onValueChange={setLocaleType}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="deal">Deal</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                        <SelectItem value="show">Show</SelectItem>
                        <SelectItem value="special">Special</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Address *</Label>
                    <Input
                      value={localeAddress}
                      onChange={(e) => setLocaleAddress(e.target.value)}
                      placeholder="Enter business address..."
                      className="bg-slate-900 border-slate-700 text-white mt-1"
                    />
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline"
                  onClick={() => setSelectedSuggestion(null)}
                  className="flex-1 border-slate-700 text-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => publishContent(selectedSuggestion)}
                  disabled={publishing || publishTo.length === 0}
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                  data-testid="confirm-publish-button"
                >
                  {publishing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Publish
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
      <FloatingDock />
    </div>
  );
}
